import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, publishReplay, refCount, take, catchError } from 'rxjs/operators';
import { UsersManagementDTO } from './users-management.dto';
import {
  UserManagementModel,
  UserChangePasswordModel,
  UserChangeCurrentUser,
} from './users-management.model';
import { HttpService } from 'src/app/services/http-service';
import { tpBuildUrl } from 'src/app/utils/url-utils';

@Injectable({ providedIn: 'root' })
export class UsersManagementService {
  private readonly call_user = tpBuildUrl('/api/Utente');
  private readonly call_user_current = tpBuildUrl('/api/Utente/Current');
  private readonly call_update_user_admin = tpBuildUrl('/api/Utente/Admin');
  private readonly call_change_password = tpBuildUrl('/api/Utente/ChangePassword');
  private readonly call_change_role = tpBuildUrl('/api/Utente/SetRole/');
  private cacheUsers$: Observable<UsersManagementDTO[]>;

  constructor(private readonly http: HttpService) {}

  public flush() {
    this.cacheUsers$ = null;
  }

  public changeRole(userId: string, roleId: string) {
    const urlToCall = this.call_change_role + userId + '?roleId=' + roleId;
    const httpOptions = this.http.getHttpOptions();
    return this.http.put<any>(urlToCall, [], httpOptions).pipe(
      map(_ => 'ruolo cambiato con successo'),
      catchError(_ => of('impossibile cambiare il ruolo')),
    );
  }

  public changePassword(oldPassword: string, newPassword: string) {
    const urlToCall = this.call_change_password;
    const httpOptions = this.http.getHttpOptions();
    const model = UserChangePasswordModel.adapt(oldPassword, newPassword);
    return this.http.put<any>(urlToCall, model, httpOptions).pipe(
      map(_ => ' Password cambiata con successo'),
      catchError(_ => of(' Impossibile cambiare la password')),
    );
  }

  public saveUser(userDto: UsersManagementDTO) {
    const urlToCall = this.call_user;
    const model = UserManagementModel.adaptFromDtoToCreate(userDto);
    const httpOptions = this.http.getHttpOptions();
    return this.http.post<UserManagementModel>(urlToCall, model, httpOptions).pipe(
      map((el: UserManagementModel) => {
        const userDtoSaved = UserManagementModel.getUserDto(el);
        this.cacheUsers$ = this.getUsers().pipe(
          map(listUser => {
            listUser.push(userDtoSaved);
            return listUser;
          }),
          publishReplay(1),
          refCount(),
          take(1),
        );
        return { res: true, messagge: 'creato con successo' };
      }),
      catchError(e => {
        console.log(e.error);
        return of({ res: false, messagge: e.error });
      }),
    );
  }

  public updateCurrentUser(userDto: UsersManagementDTO) {
    const urlToCall = this.call_user + '/' + userDto.id;
    const userModel = UserChangeCurrentUser.adapt(userDto);
    const httpOptions = this.http.getHttpOptions();
    return this.http.put<any>(urlToCall, userModel, httpOptions).pipe(
      map(_ => {
        if (this.cacheUsers$) {
          this.cacheUsers$ = this.getUsers().pipe(
            map(listUser => {
              const index = listUser.findIndex(user => user.id === userDto.id);
              listUser[index].name = userDto.name;
              listUser[index].surname = userDto.surname;
              return listUser;
            }),
            publishReplay(1),
            refCount(),
            take(1),
          );
        }
        return 'dati aggiornati con successo.';
      }),
      catchError(err => 'errore aggiornamento dati ' + err),
    );
  }

  public updateUser(userDto: UsersManagementDTO) {
    const urlToCall = this.call_update_user_admin + '/' + userDto.id;
    const userModel = UserManagementModel.adaptFromDtoToUpdate(userDto);
    const httpOptions = this.http.getHttpOptions();
    return this.http.put<any>(urlToCall, userModel, httpOptions).pipe(
      map(_ => {
        this.cacheUsers$ = this.getUsers().pipe(
          map(listUser => {
            const index = listUser.findIndex(user => user.id === userDto.id);
            listUser[index] = userDto;
            return listUser;
          }),
          publishReplay(1),
          refCount(),
          take(1),
        );
      }),
    );
  }

  public getUserInfo(id: string) {
    const urlToCall = this.call_user + '/' + id;
    const httpOptions = this.http.getHttpOptions();
    return this.http
      .get<UserManagementModel>(urlToCall, httpOptions)
      .pipe(map((element: UserManagementModel) => UserManagementModel.getUserDto(element)));
  }

  public getUserCurrent() {
    const urlToCall = this.call_user_current;
    const httpOptions = this.http.getHttpOptions();
    return this.http
      .get<UserManagementModel>(urlToCall, httpOptions)
      .pipe(map((element: UserManagementModel) => UserManagementModel.getUserDto(element)));
  }

  public getUserById(id: string): Observable<UsersManagementDTO> {
    return this.getUsers().pipe(
      take(1),
      map(usersList => usersList.filter(user => user.id === id)[0]),
    );
  }

  public deleteById(id: string) {
    const urlToCall = this.call_user + '/' + id;
    const httpOptions = this.http.getHttpOptions();
    return this.http.delete<UserManagementModel>(urlToCall, httpOptions).pipe(
      map((elementToDelete: UserManagementModel) => {
        this.cacheUsers$ = this.getUsers().pipe(
          map((el: UsersManagementDTO[]) => el.filter(user => user.id !== id)),
          publishReplay(1),
          refCount(),
          take(1),
        );
        return UserManagementModel.getUserDto(elementToDelete);
      }),
    );
  }

  public removeUserFromCache(id: string) {
    this.cacheUsers$ = this.getUsers().pipe(
      map((el: UsersManagementDTO[]) => el.filter(user => user.id !== id)),
      publishReplay(1),
      refCount(),
      take(1),
    );
  }

  public getUsers() {
    if (!this.cacheUsers$) {
      this.cacheUsers$ = this.getUsersCache();
    }
    return this.cacheUsers$;
  }

  private getUsersCache() {
    const urlToCall = this.call_user;
    const httpOptions = this.http.getHttpOptions();
    return this.http.get<UserManagementModel[]>(urlToCall, httpOptions).pipe(
      map((el: UserManagementModel[]) =>
        el.map((userModel: UserManagementModel) => UserManagementModel.getUserDto(userModel)),
      ),
      publishReplay(1),
      refCount(),
      take(1),
    );
  }
}
